import { useState, useEffect, useCallback } from 'react';
import { Shield, CheckCircle, AlertTriangle, Database, Lock, Eye } from 'lucide-react';
import { logger } from '../../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPrivacy() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPrivacy = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/privacy`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (err) { logger.error("Operation failed:", err); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchPrivacy(); }, [fetchPrivacy]);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[var(--pi-violet)] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-privacy">
      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-emerald-400" />
        <h1 className="text-xl font-bold text-white font-space">Privacy Compliance Monitor</h1>
        {data?.zero_content_violations && (
          <span className="px-3 py-1 text-xs font-bold bg-emerald-500/20 text-emerald-400 rounded-full border border-emerald-500/30">COMPLIANT</span>
        )}
      </div>
      <p className="text-white/40 text-sm">IP Critical — Enforces metadata-only behavioral model. Distinguishes from IBM US10152475B2.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Database} label="Patterns Saved" value={data?.total_patterns_saved || 0} color="violet" />
        <StatCard icon={Eye} label="Events Tracked" value={data?.total_events_tracked || 0} color="cyan" />
        <StatCard icon={Lock} label="Content Blocked" value={data?.content_fields_blocked || 0} color="emerald" />
        <StatCard icon={data?.zero_content_violations ? CheckCircle : AlertTriangle}
          label="Violations" value={data?.zero_content_violations ? "Zero" : "DETECTED"} 
          color={data?.zero_content_violations ? "emerald" : "red"} />
      </div>

      <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-6 space-y-4">
        <h3 className="text-white font-bold text-sm">Technical Privacy Guarantee</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-emerald-400 text-xs font-bold uppercase tracking-wider mb-2">What IS Stored (Metadata Only)</h4>
            <ul className="space-y-1 text-white/50 text-xs">
              {["Tone mode selected", "Rewrite accepted (bool)", "Message length (chars only)", "Receiver outcome chosen", "Relationship context", "Time of day / day of week", "Website domain", "PI Score before & after", "Profession setting"].map(item => (
                <li key={item} className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-emerald-400 shrink-0" />{item}</li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-red-400 text-xs font-bold uppercase tracking-wider mb-2">What is NEVER Stored (Code Enforced)</h4>
            <ul className="space-y-1 text-white/50 text-xs">
              {["Message text content", "Email or chat content", "Rewritten message text", "AI prompts written", "Subject lines", "Any raw input text"].map(item => (
                <li key={item} className="flex items-center gap-2"><Lock className="w-3 h-3 text-red-400 shrink-0" />{item}</li>
              ))}
            </ul>
          </div>
        </div>
        <p className="text-white/20 text-[10px] mt-4">Enforced by: <strong className="text-white/40">Code whitelist</strong> — not policy. FORBIDDEN_CONTENT_FIELDS are architecturally excluded.</p>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    violet: "text-[var(--pi-violet)] bg-[var(--pi-violet)]/10",
    cyan: "text-cyan-400 bg-cyan-500/10",
    emerald: "text-emerald-400 bg-emerald-500/10",
    red: "text-red-400 bg-red-500/10",
  };
  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
      <div className={`w-8 h-8 rounded-lg ${colors[color]} flex items-center justify-center mb-2`}><Icon className="w-4 h-4" /></div>
      <p className="text-white font-bold text-lg">{value}</p>
      <p className="text-white/30 text-xs">{label}</p>
    </div>
  );
}
